import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Camera, RefreshCw, Table2 } from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { selfieAttendanceAPI, staffAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

function padDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatTime(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function formatDateDisplay(dateStr) {
  if (!dateStr) return '—';
  return new Date(`${dateStr}T00:00:00`).toLocaleDateString('en-IN', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/** Total duration between in and out for the day (single pair). */
function totalHoursLabel(inTime, outTime) {
  if (!inTime || !outTime) return '—';
  const a = new Date(inTime).getTime();
  const b = new Date(outTime).getTime();
  if (Number.isNaN(a) || Number.isNaN(b) || b <= a) return '—';
  const totalMins = Math.round((b - a) / 60000);
  const h = Math.floor(totalMins / 60);
  const m = totalMins % 60;
  const dec = ((b - a) / 3600000).toFixed(2);
  return `${h}h ${m}m (${dec} hrs)`;
}

export default function SelfieAttendanceRecords() {
  const { user, isSuperAdmin } = useAuth();
  const isAdminView = isSuperAdmin || user?.role_name === 'Admin';
  const today = new Date();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);

  const [from, setFrom] = useState(padDate(monthStart));
  const [to, setTo] = useState(padDate(today));
  const [date, setDate] = useState('');
  const [staffList, setStaffList] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isAdminView) return;
    staffAPI
      .list({ status: 'ACTIVE' })
      .then((res) => setStaffList(res.data || []))
      .catch(() => setStaffList([]));
  }, [isAdminView]);

  const load = useCallback(() => {
    if (!date && (!from || !to || from > to)) {
      setError('Choose a valid date range (from ≤ to).');
      return;
    }
    setLoading(true);
    setError('');
    selfieAttendanceAPI
      .getReport({
        from,
        to,
        date: date || undefined,
        staffId: isAdminView ? (selectedStaff || undefined) : undefined,
      })
      .then((res) => {
        setRows(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => {
        setRows([]);
        setError(err.message || 'Could not load attendance records.');
      })
      .finally(() => setLoading(false));
  }, [date, from, to, isAdminView, selectedStaff]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-ga-text-primary flex items-center gap-2">
              <Table2 className="h-7 w-7 text-teal-600" />
              Selfie attendance records
            </h1>
            <p className="text-ga-text-muted mt-1">
              {isAdminView
                ? 'All recorded IN/OUT for this facility — filter by day and employee.'
                : 'Your own recorded IN/OUT attendance.'}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/dashboard/selfie-attendance"
              className="px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-medium hover:bg-teal-700 transition flex items-center gap-2"
            >
              <Camera className="h-4 w-4" />
              Take selfie
            </Link>
          </div>
        </div>

        <div className="ga-card rounded-xl shadow-sm border p-4 flex flex-col md:flex-row md:items-end gap-4 flex-wrap">
          <div>
            <label className="block text-xs font-medium text-ga-text-muted mb-1">Day (optional)</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="ga-input px-3 py-2 rounded-lg"
            />
          </div>
          {!date && (
            <>
              <div>
                <label className="block text-xs font-medium text-ga-text-muted mb-1">From</label>
                <input
                  type="date"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                  className="ga-input px-3 py-2 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-ga-text-muted mb-1">To</label>
                <input
                  type="date"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  className="ga-input px-3 py-2 rounded-lg"
                />
              </div>
            </>
          )}
          {isAdminView ? (
            <div>
              <label className="block text-xs font-medium text-ga-text-muted mb-1">Employee</label>
              <select
                value={selectedStaff}
                onChange={(e) => setSelectedStaff(e.target.value)}
                className="ga-input px-3 py-2 rounded-lg min-w-[220px]"
              >
                <option value="">All employees</option>
                {staffList.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} ({s.role})
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div>
              <label className="block text-xs font-medium text-ga-text-muted mb-1">Employee</label>
              <div className="ga-input px-3 py-2 rounded-lg min-w-[220px] bg-ga-bg-surface-subtle/40">
                My attendance
              </div>
            </div>
          )}
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-ga-border-subtle text-ga-text-primary font-medium hover:bg-ga-bg-surface-subtle/40 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 text-red-800 text-sm px-4 py-3">{error}</div>
        )}

        {!loading && !error && rows.length === 0 && (
          <div className="ga-card rounded-xl shadow-sm border py-16 text-center text-ga-text-muted">
            <Calendar className="h-12 w-12 mx-auto mb-3 opacity-40" />
            <p className="font-medium text-ga-text-primary">No records in this range</p>
            <p className="text-sm mt-1">Try widening the dates or record attendance from Take selfie.</p>
          </div>
        )}

        {(loading || rows.length > 0) && (
          <div className="ga-card rounded-xl shadow-sm border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-ga-border-subtle bg-ga-bg-surface-subtle/40 text-left">
                    <th className="px-4 py-3 font-semibold text-ga-text-primary">Date</th>
                    <th className="px-4 py-3 font-semibold text-ga-text-primary">Name</th>
                    <th className="px-4 py-3 font-semibold text-ga-text-primary">IN</th>
                    <th className="px-4 py-3 font-semibold text-ga-text-primary">OUT</th>
                    <th className="px-4 py-3 font-semibold text-ga-text-primary">Total (day)</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-12 text-center text-ga-text-muted">
                        Loading…
                      </td>
                    </tr>
                  ) : (
                    rows.map((rec) => (
                      <tr
                        key={rec.id}
                        className="border-b border-ga-border-subtle last:border-0 hover:bg-ga-bg-surface-subtle/30"
                      >
                        <td className="px-4 py-3 text-ga-text-primary whitespace-nowrap">
                          {formatDateDisplay(rec.date)}
                        </td>
                        <td className="px-4 py-3 text-ga-text-primary">
                          <span className="font-medium">{rec.staff_name || '—'}</span>
                          {rec.staff_role && (
                            <span className="text-ga-text-muted text-xs ml-1">({rec.staff_role})</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-ga-text-primary whitespace-nowrap">
                          {formatTime(rec.in_time)}
                        </td>
                        <td className="px-4 py-3 text-ga-text-primary whitespace-nowrap">
                          {formatTime(rec.out_time)}
                        </td>
                        <td className="px-4 py-3 text-ga-text-primary whitespace-nowrap">
                          {totalHoursLabel(rec.in_time, rec.out_time)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
